/* App shell: sidebar nav, top header, route switching */
import React from 'react';
import { Icon, Avatar } from './primitives.jsx';
import { useAnchoredPosition, HIDDEN_ANCHORED_STYLE } from '../lib/use-anchored-position.jsx';
import { useRouteTitle } from './route-title-context.jsx';
import { listNotifications, markNotificationRead, markAllNotificationsRead } from '../api.js';

/* Build identifiers, injected by Vite at build time (see vite.config.ts).
   Used to prove which bundle is live when a user reports a stale UI
   and to help debug deploy pipeline issues from a screenshot. */
export const BUILD_SHA  = typeof __BUILD_SHA__  !== 'undefined' ? __BUILD_SHA__  : 'dev';
export const BUILD_TIME = typeof __BUILD_TIME__ !== 'undefined' ? __BUILD_TIME__ : 'dev';
export const BUILD_SHA_SHORT = String(BUILD_SHA).slice(0, 7);
if (typeof window !== 'undefined') {
  console.info(`[eOS] build ${BUILD_SHA_SHORT} (${BUILD_TIME})`);
  window.__BUILD_SHA__ = BUILD_SHA;
  window.__BUILD_TIME__ = BUILD_TIME;
}

// Nav is grouped into sections; flatten for route lookup but render
// the headers in the sidebar. `adminOnly: true` items hide unless
// the viewer's role is 'admin'.
export const NAV_SECTIONS = [
  {
    id: 'dash',
    label: 'My Work',
    items: [
      { id: 'chat',       label: 'Chats',                 icon: 'chat'    },
      { id: 'agents',     label: 'Agents',                icon: 'sparkle' },
      { id: 'reports',    label: 'Artifacts',             icon: 'file'    },
      { id: 'logs',       label: 'My Logs',               icon: 'time'    },
    ],
  },
  {
    id: 'directory',
    label: 'Knowledge base',
    items: [
      { id: 'news',       label: 'Morning Briefing', icon: 'book'      },
      { id: 'people',     label: 'People',     icon: 'profile'   },
      { id: 'projects',   label: 'Projects',   icon: 'briefcase' },
      { id: 'clients',    label: 'Clients',    icon: 'briefcase' },
      { id: 'services',   label: 'Services',   icon: 'sparkle'   },
      { id: 'industries', label: 'Industries', icon: 'compass'   },
      { id: 'organizations', label: 'Organizations', icon: 'briefcase' },
      { id: 'offices',    label: 'Offices',    icon: 'house'     },
    ],
  },
  {
    id: 'management',
    label: 'Management',
    items: [
      { id: 'staffing',   label: 'Staffing',   icon: 'time'      },
    ],
  },
  {
    id: 'admin',
    label: 'Admin',
    adminOnly: true,
    items: [
      { id: 'admin',       label: 'Admin dashboard', icon: 'shield'  },
      { id: 'orgrepo',     label: 'OrgRepo',         icon: 'git'     },
      { id: 'admin-logs',  label: 'Admin Logs',      icon: 'time'    },
      { id: 'onboarding',  label: 'The Manual',       icon: 'book'    },
    ],
  },
];
export const NAV = NAV_SECTIONS.flatMap(s => s.items);

const UnreadBadge = ({ n }) => {
  if (!n || n <= 0) return null;
  return (
    <span style={{
      marginLeft:'auto',
      fontSize:10,fontWeight:700,
      minWidth:18,height:18,padding:'0 6px',borderRadius:9,
      background:'var(--orange)',color:'#fff',
      display:'inline-flex',alignItems:'center',justifyContent:'center',
      lineHeight:1,
    }}>{n > 99 ? '99+' : n}</span>
  );
};

export const Sidebar = ({ route, onRoute, user, sidebar, onSignOut, unread = {}, mobileOpen = false }) => (
  <div className={`sidebar ${sidebar === 'compact' ? 'sidebar--compact' : ''} ${mobileOpen ? 'is-open' : ''}`}>
    <div className="sidebar__brand">
      <div className="brand-mark">
        <img src="/assets/eraneos-wordmark.svg?v=2" alt="Eraneos" style={{height:24,display:'block'}}/>
      </div>
    </div>

    <div className="sidebar__nav">
      {NAV_SECTIONS.filter(s => !s.adminOnly || user.role === 'admin').map((section, sectionIdx) => (
        <React.Fragment key={section.id}>
          {sectionIdx > 0 && (
            <div style={{height:1,background:'var(--border)',margin:'10px 14px'}}/>
          )}
          <div style={{fontSize:10,letterSpacing:'0.12em',color:'var(--fg-muted)',textTransform:'uppercase',fontWeight:700,padding:'4px 14px 6px'}}>
            {section.label}
          </div>
          {section.items.map(n => (
            <div key={n.id} className={`sidebar__item ${route === n.id ? 'on' : ''}`} onClick={() => onRoute(n.id)}>
              <Icon name={n.icon} size={15}/>
              <span>{n.label}</span>
              {route !== n.id && <UnreadBadge n={unread[n.id]}/>}
            </div>
          ))}
        </React.Fragment>
      ))}
    </div>

    <div className="sidebar__foot">
      <div
        onClick={() => onRoute?.('profile')}
        title="Open your profile"
        style={{
          display:'flex',alignItems:'center',gap:10,padding:10,
          borderRadius:10,background:'var(--bg-alt)',
          cursor:'pointer',
          outline: route === 'profile' ? '2px solid var(--orange)' : 'none',
          outlineOffset: -2,
        }}
      >
        <Avatar src={user.img} name={user.name}/>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:12.5,fontWeight:500,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{user.name}</div>
          <div style={{fontSize:11,color:'var(--fg-muted)',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{user.title || (user.role === 'admin' ? 'Admin' : user.role === 'eOS-Manager' ? 'eOS-Manager' : 'Member')}</div>
        </div>
        {onSignOut && (
          <button
            onClick={e => { e.stopPropagation(); onSignOut(); }}
            title="Sign out"
            aria-label="Sign out"
            style={{display:'flex',alignItems:'center',justifyContent:'center',background:'transparent',border:0,color:'var(--fg-muted)',cursor:'pointer',padding:6,borderRadius:6}}
          ><Icon name="log-out" size={15}/></button>
        )}
      </div>
      {/* Build identifier moved to the live ticker (see App.jsx). */}
    </div>
  </div>
);

// Map non-NAV routes (skills, workers, etc.) to friendly labels so
// the breadcrumb doesn't have to fall back to the bare hash.
const EXTRA_ROUTE_LABELS = {
  skills:    { label: 'Skills',    section: 'Skills'    },
  workers:   { label: 'Workers',   section: 'Skills'    },
  profile:   { label: 'Profile',   section: 'My Work' },
};

// Deck / memo viewer routes are deep, id-keyed surfaces (`#runs/<id>/deck`,
// `#chat-artefacts/<id>/memo`). They fully describe their own breadcrumb
// trail via useSubCrumbs (a working parent link + the run's title), so we
// suppress both the auto route crumb — its href would be the bare,
// id-less route, which renders an empty "Missing run id" page — and the
// raw-subPath pretty-print fallback, which would otherwise surface the
// run-id UUID as a cryptic crumb label.
const VIEWER_ROUTES = new Set(['runs', 'chat-artefacts']);

/** Build the breadcrumb segments for a (route, subPath). Each
 *  segment is { label, href } — href === null means non-clickable
 *  (current page or root). */
/**
 * Find the first NAV item id for a given section — used as the section
 * row's landing href so the breadcrumb's section segment is clickable
 * (goes to the first route within the section).
 */
function sectionLandingRoute(sectionLabel) {
  const section = NAV_SECTIONS.find(s => s.label === sectionLabel);
  return section?.items[0]?.id || null;
}

function breadcrumbSegments(route, subPath, routeTitle, subCrumbs) {
  // Home / eOS → goes to the first nav route (News today).
  const homeHref = NAV[0]?.id || null;
  const out = [{ label: 'eOS', href: homeHref }];

  // Chat: flatten to "eOS / Chat / <chat title>" so it stays
  // short on mobile. The chat title comes from RouteTitleContext —
  // ChatView pushes it whenever a chat is opened.
  if (route === 'chat') {
    if (routeTitle && routeTitle.trim()) {
      out.push({ label: 'Chat', href: 'chat' });
      out.push({ label: routeTitle.trim(), href: null });
    } else {
      out.push({ label: 'Chat', href: null });
    }
    return out;
  }

  // Slide system is admin-only, reached from the Admin dashboard:
  // eOS / Admin dashboard / Slide system / <level/selection>.
  if (route === 'slide-sandbox') {
    const has = (subCrumbs && subCrumbs.length > 0) || (subPath && subPath.length > 0);
    out.push({ label: 'Admin dashboard', href: 'admin' });
    out.push({ label: 'Slide system', href: has ? 'slide-sandbox' : null });
    if (subCrumbs && subCrumbs.length) for (const c of subCrumbs) { if (c && c.label) out.push({ label: String(c.label).slice(0, 80), href: c.href || null }); }
    return out;
  }

  const navItem = NAV.find(n => n.id === route);
  const section = navItem ? NAV_SECTIONS.find(s => s.items.some(i => i.id === route)) : null;
  const hasSub = (subPath && subPath.length > 0)
              || (routeTitle && routeTitle.trim().length > 0)
              || (subCrumbs && subCrumbs.length > 0);

  if (section) {
    const sectionHref = sectionLandingRoute(section.label);
    out.push({ label: section.label, href: sectionHref });
  } else if (EXTRA_ROUTE_LABELS[route]) {
    const sectionLabel = EXTRA_ROUTE_LABELS[route].section;
    out.push({ label: sectionLabel, href: sectionLandingRoute(sectionLabel) });
  }

  if (navItem) {
    out.push({ label: navItem.label, href: hasSub ? route : null });
  } else if (EXTRA_ROUTE_LABELS[route]) {
    out.push({ label: EXTRA_ROUTE_LABELS[route].label, href: hasSub ? route : null });
  } else if (route && !VIEWER_ROUTES.has(route)) {
    out.push({ label: route.charAt(0).toUpperCase() + route.slice(1), href: hasSub ? route : null });
  }

  // Context-driven sub-crumbs (a view's internal selection state —
  // active tab, selected agent, opened run, etc.) take precedence
  // over the raw subPath head: when the page is providing labels,
  // they're inevitably better than `pretty-print(id-slug)`.
  if (subCrumbs && subCrumbs.length > 0) {
    for (const c of subCrumbs) {
      if (!c || !c.label) continue;
      out.push({ label: String(c.label).slice(0, 80), href: c.href || null });
    }
  } else if (subPath && !VIEWER_ROUTES.has(route)) {
    // Fallback for routes that haven't adopted the context API yet —
    // pretty-print the first path segment. Skipped for viewer routes so
    // a still-loading deck/memo never flashes the raw run-id UUID.
    const head = subPath.split('/')[0];
    const pretty = head.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).slice(0, 80);
    out.push({ label: pretty, href: null });
  }

  if (routeTitle && routeTitle.trim()) {
    out.push({ label: routeTitle.trim(), href: null });
  }

  return out;
}

/* Viewport-aware breadcrumb. Renders all segments; if the total
 * exceeds the container width, leading segments collapse into a
 * single "…" indicator until the rightmost (deepest, most relevant)
 * segments fit. Re-measures on resize + segment change.
 *
 * Implementation: a useLayoutEffect that walks scrollWidth vs
 * clientWidth after every render. If overflowing, increment
 * `hiddenCount` to drop the next leading segment, and re-render.
 * Capped at segments.length-1 so the deepest segment is never
 * dropped — even if it alone doesn't fit, it gets ellipsised by
 * CSS text-overflow. */
const Breadcrumb = ({ segments, onGo }) => {
  const navRef = React.useRef(null);
  const [hiddenCount, setHiddenCount] = React.useState(0);

  // Reset hidden count whenever the segment list changes — the new set
  // might fit fully even if the old one had to truncate.
  React.useLayoutEffect(() => {
    setHiddenCount(0);
  }, [segments.map(s => s.label).join('|')]);

  // After every render, if we still overflow, drop one more leading
  // segment. Cap at segments.length-1 so the deepest always renders.
  React.useLayoutEffect(() => {
    const el = navRef.current;
    if (!el) return;
    if (el.scrollWidth > el.clientWidth + 1 && hiddenCount < segments.length - 1) {
      setHiddenCount(c => c + 1);
    }
  });

  // Re-measure on window resize.
  React.useEffect(() => {
    const onResize = () => setHiddenCount(0);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const visible = segments.slice(hiddenCount);
  return (
    <nav ref={navRef} className="topbar__crumb" aria-label="Breadcrumb">
      {hiddenCount > 0 && (
        <>
          <span className="topbar__crumb-ellipsis" title={segments.slice(0, hiddenCount).map(s => s.label).join(' / ')}>…</span>
          <span className="topbar__crumb-sep">/</span>
        </>
      )}
      {visible.map((seg, i) => {
        const isLast = i === visible.length - 1;
        return (
          <React.Fragment key={hiddenCount + i}>
            {i > 0 && <span className="topbar__crumb-sep">/</span>}
            {seg.href
              ? <a className="topbar__crumb-link" onClick={() => onGo(seg.href)}>{seg.label}</a>
              : <span className={isLast ? 'topbar__crumb-current' : 'topbar__crumb-static'}>{seg.label}</span>}
          </React.Fragment>
        );
      })}
    </nav>
  );
};

export const TopHeader = ({ route, subPath = '', tweaksTrigger, onToggleNav, onRoute, messagesUnread = 0 }) => {
  const { title: routeTitle, subCrumbs } = useRouteTitle();
  const segments = breadcrumbSegments(route, subPath, routeTitle, subCrumbs);
  const goRoute = id => { if (id) location.hash = id; };
  const openChats = () => (onRoute ? onRoute('chat') : (location.hash = 'chat'));

  return (
    <div className="topbar">
      {onToggleNav && (
        <button
          type="button"
          className="topbar__hamburger"
          onClick={onToggleNav}
          aria-label={messagesUnread > 0 ? `Open menu — ${messagesUnread} unread` : 'Open menu'}
        >
          <Icon name="menu" size={18}/>
          {/* Mirror unread messages onto the top-left symbol so the
              signal is visible on mobile, where the sidebar (with the
              Chats badge) is collapsed behind this button. */}
          {messagesUnread > 0 && <span className="topbar__hamburger-dot" aria-hidden="true"/>}
        </button>
      )}
      <Breadcrumb segments={segments} onGo={goRoute}/>
      {/* Unread-messages bell-style affordance: appears only when there
          are unread chats; click jumps to Chats. Visible on mobile too,
          where the sidebar Chats badge is hidden. */}
      <MessagesButton count={messagesUnread} onOpen={openChats}/>
      <NotificationBell/>
      {tweaksTrigger}
    </div>
  );
};

/* Topbar "unread messages" icon — same badge treatment as the
 * notification bell, but for unread chat messages. Only rendered when
 * there's at least one unread chat. */
const MessagesButton = ({ count = 0, onOpen }) => {
  if (!count || count <= 0) return null;
  return (
    <button
      type="button"
      onClick={onOpen}
      title={`${count} unread message${count === 1 ? '' : 's'}`}
      aria-label={`${count} unread messages`}
      style={{
        all:'unset',cursor:'pointer',
        padding:'6px 8px',borderRadius:6,
        display:'inline-flex',alignItems:'center',
        position:'relative',
      }}
    >
      <Icon name="chat" size={18}/>
      <span style={{
        position:'absolute',top:0,right:0,
        minWidth:16,height:16,padding:'0 4px',borderRadius:8,
        background:'var(--orange)',color:'#fff',
        fontSize:10,fontWeight:700,
        display:'inline-flex',alignItems:'center',justifyContent:'center',
        lineHeight:1,
      }}>{count > 99 ? '99+' : count}</span>
    </button>
  );
};

/* ─── Notification bell ──────────────────────────────────────────
 * Toolbar bell + dropdown. Polls /api/me/notifications every 30 s
 * so unread reminders / agent-run completions / reviewer answers
 * surface without the user having to refresh. Click a row to mark
 * it read (and follow actionUrl when present); a "Mark all read"
 * link at the top of the dropdown clears the badge.
 *
 * Bridges the gap where the notifications backend was complete
 * (write path used by reminders, agent-execution, reviewer
 * collaboration) but no UI consumed them — every Phase 4 / Phase 5
 * notification was silently piling up in D1 until this commit.
 */
const NotificationBell = () => {
  const [items, setItems] = React.useState([]);
  const [unread, setUnread] = React.useState(0);
  const [open, setOpen] = React.useState(false);
  const [error, setError] = React.useState(null);
  const rootRef = React.useRef(null);
  const pos = useAnchoredPosition(rootRef, open, { align: 'end', width: 380, maxHeight: 480 });

  const refresh = React.useCallback(() => {
    listNotifications(50)
      .then(({ notifications, unread: u }) => {
        setItems(notifications || []);
        setUnread(u || 0);
        setError(null);
      })
      .catch(err => setError(err.message || String(err)));
  }, []);

  React.useEffect(() => {
    refresh();
    const t = setInterval(refresh, 30_000);
    return () => clearInterval(t);
  }, [refresh]);

  // Close on click-outside.
  React.useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const onItemClick = async (n) => {
    if (!n.readAt) {
      try { await markNotificationRead(n.id); } catch { /* ignore */ }
    }
    if (n.actionUrl) {
      if (n.actionUrl.startsWith('#')) {
        window.location.hash = n.actionUrl;
        setOpen(false);
      } else if (n.actionUrl.startsWith('/api/photos/')) {
        // Binary artefact link — open in a new tab so the download
        // doesn't navigate away from the app.
        window.open(n.actionUrl, '_blank', 'noopener,noreferrer');
      } else if (n.actionUrl.startsWith('/')) {
        // In-app route (e.g. '/agents' from the Proposal Builder
        // reminders) — fold into the hash router.
        window.location.hash = n.actionUrl.replace(/^\//, '#');
        setOpen(false);
      } else {
        window.open(n.actionUrl, '_blank', 'noopener,noreferrer');
      }
    }
    refresh();
  };

  const onMarkAll = async () => {
    try {
      await markAllNotificationsRead();
      refresh();
    } catch (err) {
      setError(err.message || String(err));
    }
  };

  const severityColor = (s) => s === 'high'
    ? '#A20235'
    : s === 'medium'
      ? '#B83C0F'
      : 'var(--fg-muted)';

  return (
    <div ref={rootRef} style={{position:'relative'}}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        title={unread > 0 ? `${unread} unread notifications` : 'Notifications'}
        style={{
          all:'unset',cursor:'pointer',
          padding:'6px 8px',borderRadius:6,
          display:'inline-flex',alignItems:'center',gap:4,
          position:'relative',
        }}
        aria-label="Notifications"
      >
        <Icon name="bell" size={18}/>
        {unread > 0 && (
          <span style={{
            position:'absolute',top:0,right:0,
            minWidth:16,height:16,padding:'0 4px',borderRadius:8,
            background:'var(--orange)',color:'#fff',
            fontSize:10,fontWeight:700,
            display:'inline-flex',alignItems:'center',justifyContent:'center',
            lineHeight:1,
          }}>{unread > 99 ? '99+' : unread}</span>
        )}
      </button>
      {open && (
        <div style={{
          overflowY:'auto',
          background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:8,
          boxShadow:'0 10px 30px rgba(0,0,0,0.18)',
          zIndex:1000,
          ...(pos || HIDDEN_ANCHORED_STYLE),
        }}>
          <div style={{
            display:'flex',alignItems:'center',justifyContent:'space-between',
            padding:'10px 14px',borderBottom:'1px solid var(--border)',
          }}>
            <span style={{fontSize:11,fontWeight:600,letterSpacing:'0.06em',textTransform:'uppercase',color:'var(--fg-muted)'}}>
              Notifications {unread > 0 && <span style={{color:'var(--orange)'}}>· {unread} unread</span>}
            </span>
            {unread > 0 && (
              <button
                type="button"
                onClick={onMarkAll}
                style={{all:'unset',cursor:'pointer',fontSize:11,color:'var(--orange)'}}
              >Mark all read</button>
            )}
          </div>
          {error && <div style={{padding:'10px 14px',color:'#A20235',fontSize:12}}>{error}</div>}
          {items.length === 0 && !error && (
            <div style={{padding:'24px 14px',textAlign:'center',color:'var(--fg-muted)',fontSize:12.5}}>
              No notifications yet.
            </div>
          )}
          {items.map(n => (
            <div
              key={n.id}
              onClick={() => onItemClick(n)}
              style={{
                padding:'10px 14px',cursor:n.actionUrl || !n.readAt ? 'pointer' : 'default',
                borderBottom:'1px solid var(--border)',
                background: n.readAt ? 'transparent' : 'rgba(255,100,40,0.05)',
                display:'flex',flexDirection:'column',gap:4,
              }}
            >
              <div style={{display:'flex',alignItems:'baseline',gap:6}}>
                <span style={{
                  fontSize:9,padding:'1px 6px',borderRadius:3,
                  background: severityColor(n.severity),
                  color:'#fff',fontWeight:700,letterSpacing:'0.04em',textTransform:'uppercase',
                }}>{n.kind.split('.')[0]}</span>
                <span style={{fontSize:13,fontWeight:600,flex:1}}>{n.title}</span>
                <span style={{fontSize:10.5,color:'var(--fg-muted)'}}>{relTimeShort(n.createdAt)}</span>
              </div>
              {n.body && <div style={{fontSize:12,color:'var(--fg-muted)',lineHeight:1.45,whiteSpace:'pre-wrap'}}>{n.body}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

function relTimeShort(value) {
  if (!value) return '';
  const d = new Date(value);
  const abs = Math.abs(Date.now() - d.getTime());
  const min = 60_000, hr = 60 * min, day = 24 * hr;
  if (abs < min)     return 'now';
  if (abs < hr)      return `${Math.round(abs / min)}m`;
  if (abs < day)     return `${Math.round(abs / hr)}h`;
  if (abs < 7 * day) return `${Math.round(abs / day)}d`;
  return d.toLocaleDateString();
}

